/**
 * Location data processing utilities
 * Centralizes common location data transformation patterns
 */

/**
 * Standard location format used throughout the application
 */
export interface StandardLocation {
  position: {
    lat: number;
    lng: number;
  };
  address: string;
  locationName: string;
  details?: string;
  
  // Optional metadata
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  formattedAddress?: string;
}

/**
 * API address format used when saving to database
 */
export interface ApiAddress {
  id?: string;
  street: string;
  locationName: string;
  details?: string;
  latitude: number;
  longitude: number;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  contactName?: string;
  contactPhone?: string;
  isDefault?: boolean;
}

/**
 * Convert location from various source formats to standard internal format
 * This handles the various formats that might come from Google Maps, localStorage, or our own components
 */
export const standardizeLocation = (locationData: any): StandardLocation => {
  if (!locationData) {
    throw new Error('Cannot standardize null or undefined location data');
  }
  
  // Handle if input is already standardized
  if (locationData.position && typeof locationData.position.lat === 'number') {
    return {
      ...locationData,
      // Ensure these standard fields exist
      address: locationData.address || '',
      locationName: locationData.locationName || ''
    };
  }
  
  // Extract position data from various formats
  const position = {
    lat: extractNumber(locationData, ['latitude', 'lat', 'position.lat']),
    lng: extractNumber(locationData, ['longitude', 'lng', 'position.lng'])
  };
  
  // Extract address data from various formats
  const address = extractString(locationData, [
    'address',
    'formatted_address',
    'formattedAddress',
    'street',
    'details'
  ]);
  
  // Extract location name from various formats
  const locationName = extractString(locationData, [
    'locationName',
    'name',
    'place_name',
    'placeName'
  ]);
  
  // Extract optional metadata
  const city = extractString(locationData, ['city', 'locality']);
  const state = extractString(locationData, ['state', 'province', 'administrative_area']);
  const country = extractString(locationData, ['country']);
  const zipCode = extractString(locationData, ['zipCode', 'postal_code', 'postalCode']);
  
  // Construct standardized location object
  const standardLocation: StandardLocation = {
    position,
    address,
    locationName
  };
  
  // Add optional fields if they exist
  if (city) standardLocation.city = city;
  if (state) standardLocation.state = state;
  if (country) standardLocation.country = country;
  if (zipCode) standardLocation.zipCode = zipCode;
  
  // Add details if available
  const details = extractString(locationData, ['details', 'description']);
  if (details) standardLocation.details = details;
  
  return standardLocation;
};

/**
 * Convert standard location to API format for saving
 */
export const prepareForSaving = (location: StandardLocation, additionalData: Partial<ApiAddress> = {}): ApiAddress => {
  return {
    street: location.address,
    locationName: location.locationName,
    latitude: location.position.lat,
    longitude: location.position.lng,
    city: location.city,
    state: location.state,
    country: location.country,
    zipCode: location.zipCode,
    details: location.details,
    // Merge with additional data
    ...additionalData
  };
};

/**
 * Extract formatted address parts from a Google geocoding result
 */
export const extractAddressComponents = (result: google.maps.GeocoderResult): Record<string, string> => {
  if (!result || !result.address_components) {
    return {};
  }
  
  const components: Record<string, string> = {};
  
  // Define address component mappings
  const componentMappings: Record<string, string> = {
    'street_number': 'streetNumber',
    'route': 'street',
    'sublocality_level_1': 'neighborhood',
    'locality': 'city',
    'administrative_area_level_1': 'state',
    'country': 'country',
    'postal_code': 'zipCode'
  };
  
  // Process each address component
  result.address_components.forEach(component => {
    for (const type of component.types) {
      if (componentMappings[type]) {
        components[componentMappings[type]] = component.long_name;
      }
    }
  });
  
  return components;
};

/**
 * Create a full address string from address components
 */
export const formatFullAddress = (components: Record<string, string>): string => {
  const parts = [];
  
  // Add street address
  if (components.streetNumber && components.street) {
    parts.push(`${components.streetNumber} ${components.street}`);
  } else if (components.street) {
    parts.push(components.street);
  }
  
  // Add neighborhood
  if (components.neighborhood) {
    parts.push(components.neighborhood);
  }
  
  // Add city
  if (components.city) {
    parts.push(components.city);
  }
  
  // Add state and zip code
  if (components.state && components.zipCode) {
    parts.push(`${components.state} ${components.zipCode}`);
  } else if (components.state) {
    parts.push(components.state);
  }
  
  // Add country
  if (components.country) {
    parts.push(components.country);
  }
  
  return parts.join(', ');
};

/**
 * Extract location name from geocode result
 */
export const extractLocationName = (result: google.maps.GeocoderResult): string => {
  if (!result) return 'Selected Location';
  
  if (result.address_components) {
    // Try to get establishment or point_of_interest first
    const poiComponent = result.address_components.find(component => 
      component.types.includes('establishment') || 
      component.types.includes('point_of_interest')
    );
    
    if (poiComponent) {
      return poiComponent.long_name;
    }
    
    // Fallback to premise or subpremise
    const premiseComponent = result.address_components.find(component => 
      component.types.includes('premise') || 
      component.types.includes('subpremise')
    );
    
    if (premiseComponent) {
      return premiseComponent.long_name;
    }
  }
  
  // Fallback to first part of formatted address
  return result.formatted_address?.split(',')[0] || 'Selected Location';
};

/**
 * Check if two locations are nearly identical based on their coordinates
 */
export const areLocationsNearlyIdentical = (
  location1: { lat: number; lng: number }, 
  location2: { lat: number; lng: number },
  threshold = 0.0001 // Default threshold is about 11 meters at the equator
): boolean => {
  return Math.abs(location1.lat - location2.lat) < threshold && 
         Math.abs(location1.lng - location2.lng) < threshold;
};

/**
 * Save temporary location to localStorage
 */
export const saveTemporaryLocation = (location: StandardLocation): void => {
  try {
    localStorage.setItem('temp_selected_location', JSON.stringify(location));
  } catch (error) {
    console.error('Error saving temporary location:', error);
  }
};

/**
 * Get temporary location from localStorage
 */
export const getTemporaryLocation = (): StandardLocation | null => {
  try {
    const data = localStorage.getItem('temp_selected_location');
    if (!data) return null;
    
    const parsed = JSON.parse(data);
    return standardizeLocation(parsed);
  } catch (error) {
    console.error('Error getting temporary location:', error);
    return null;
  }
};

/**
 * Clear temporary location from localStorage
 */
export const clearTemporaryLocation = (): void => {
  try {
    localStorage.removeItem('temp_selected_location');
  } catch (error) {
    console.error('Error clearing temporary location:', error);
  }
};

// Utility functions for extracting values from nested objects

/**
 * Extract a string value from a property that could have different names across different objects
 */
function extractString(obj: any, possibleNames: string[]): string {
  for (const name of possibleNames) {
    // Handle nested properties with dot notation
    if (name.includes('.')) {
      const parts = name.split('.');
      let current = obj;
      let found = true;
      
      for (const part of parts) {
        if (current && typeof current === 'object' && part in current) {
          current = current[part];
        } else {
          found = false;
          break;
        }
      }
      
      if (found && typeof current === 'string') {
        return current;
      }
    } 
    // Handle direct properties
    else if (obj && typeof obj === 'object' && name in obj && typeof obj[name] === 'string') {
      return obj[name];
    }
  }
  
  return '';
}

/**
 * Extract a number value from a property that could have different names across different objects
 */
function extractNumber(obj: any, possibleNames: string[]): number {
  for (const name of possibleNames) {
    // Handle nested properties with dot notation
    if (name.includes('.')) {
      const parts = name.split('.');
      let current = obj;
      let found = true;
      
      for (const part of parts) {
        if (current && typeof current === 'object' && part in current) {
          current = current[part];
        } else {
          found = false;
          break;
        }
      }
      
      if (found && (typeof current === 'number' || !isNaN(parseFloat(current)))) {
        return typeof current === 'number' ? current : parseFloat(current);
      }
    } 
    // Handle direct properties
    else if (obj && typeof obj === 'object' && name in obj) {
      const value = obj[name];
      if (typeof value === 'number') {
        return value;
      } else if (typeof value === 'string' && !isNaN(parseFloat(value))) {
        return parseFloat(value);
      }
    }
  }
  
  return 0;
} 